'use client';

import { fetchGoodsList } from '@/api/product';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import FaqAccordion from '../../components/FaqAccordion';
import { useTransitionTechnologyTracking } from '../hooks/useTransitionTechnologyTracking';
import { TransitionTechnologyData } from '../types';
import { LensPart } from './LensPart';
import TransitionBanner from './TransitionBanner';

interface TransitionTechnologyClientProps {
  pageData: TransitionTechnologyData;
}

export default function TransitionTechnologyClient({ pageData }: TransitionTechnologyClientProps) {
  // App相关状态
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const currency = useStore((state) => state.currency);
  // 商品相关状态
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [lensSeriesProducts, setLensSeriesProducts] = useState<{
    series1: any[];
    series2: any[];
    series3: any[];
  }>({
    series1: [],
    series2: [],
    series3: [],
  });

  // ✅ 使用专门的埋点Hook
  const {
    trackTransitionKnowledgePageView,
    trackTransitionKnowledgePageProductExp,
    trackTransitionKnowledgePageProductClick,
    trackTransitionKnowledgePageBannerClick,
    trackTransitionKnowledgePageBannerExp,
    trackTransitionKnowledgePageMktExp,
  } = useTransitionTechnologyTracking();

  // 镜片系列曝光监听
  const [lensSeriesOneRef, lensSeriesOneInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const [lensSeriesTwoRef, lensSeriesTwoInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const [lensSeriesThreeRef, lensSeriesThreeInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  // 获取商品列表数据
  const fetchAllProducts = async () => {
    const groupId = pageData?.group_id;

    if (!groupId || allProducts.length > 0) return;

    try {
      const { data } = await fetchGoodsList({
        currency: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
        group_id: parseInt(groupId),
        limit: 12,
        page: 1,
        in_stock: 1,
        is_sale: 1,
        append_correlation: 1,
      });

      const productList = data?.list || [];
      setAllProducts(productList);

      // 将12个商品分配给3个镜片系列，每个系列4个
      setLensSeriesProducts({
        series1: productList.slice(0, 4),
        series2: productList.slice(4, 8),
        series3: productList.slice(8, 12),
      });
    } catch (error) {
      logger.error('获取商品列表失败:', error);
    }
  };

  // 在组件挂载时获取商品数据
  useEffectWithCurReady(() => {
    fetchAllProducts();
  }, [pageData?.group_id, currency]);

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
      window.addEventListener('appView', trackTransitionKnowledgePageView);
      return () => {
        window.removeEventListener('appView', trackTransitionKnowledgePageView);
      };
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('App登录token:', _token);
          if (_token !== 'ClearToken') {
            setToken(_token);
            // 获取用户信息
            await refreshAccountInfo();
          } else {
            // 清空当前用户信息
            setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面加载和卸载时的埋点处理
  useEffect(() => {
    // 页面加载时发送View事件
    trackTransitionKnowledgePageView();
  }, [trackTransitionKnowledgePageView]);

  const [faqRef, faqInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // FAQ区域曝光埋点
  useEffect(() => {
    if (faqInView) {
      trackTransitionKnowledgePageMktExp({ mkt_name: 'FAQ' });
    }
  }, [faqInView, trackTransitionKnowledgePageMktExp]);

  // 镜片系列曝光埋点
  useEffect(() => {
    if (lensSeriesOneInView) {
      trackTransitionKnowledgePageMktExp({
        mkt_name: pageData.Lens_series1?.mk_name || 'gens',
      });
    }
  }, [lensSeriesOneInView, trackTransitionKnowledgePageMktExp, pageData.Lens_series1?.mk_name]);

  useEffect(() => {
    if (lensSeriesTwoInView) {
      trackTransitionKnowledgePageMktExp({
        mkt_name: pageData.Lens_series2?.mk_name || 'xtractive',
      });
    }
  }, [lensSeriesTwoInView, trackTransitionKnowledgePageMktExp, pageData.Lens_series2?.mk_name]);

  useEffect(() => {
    if (lensSeriesThreeInView) {
      trackTransitionKnowledgePageMktExp({
        mkt_name: pageData.Lens_series3?.mk_name || 'polarized',
      });
    }
  }, [lensSeriesThreeInView, trackTransitionKnowledgePageMktExp, pageData.Lens_series3?.mk_name]);

  return (
    <div className="w-full bg-white relative">
      {/* Banner区域 */}
      {pageData.banner && (
        <TransitionBanner
          id="banner-part"
          bannerData={pageData.banner}
          onBannerClick={trackTransitionKnowledgePageBannerClick}
          onBannerExp={trackTransitionKnowledgePageBannerExp}
        />
      )}

      {/* 镜片系列1 */}
      {pageData.Lens_series1 && (
        <div ref={lensSeriesOneRef}>
          <LensPart
            lensPartData={{
              ...pageData.Lens_series1,
              lists: lensSeriesProducts.series1,
              group_id: pageData.group_id, // 统一跳转目标
            }}
            onProductClick={(product, index) =>
              trackTransitionKnowledgePageProductClick({ item: product, index })
            }
            onProductExp={(product, index) =>
              trackTransitionKnowledgePageProductExp({ item: product, index })
            }
          />
        </div>
      )}

      {/* 镜片系列2 */}
      {pageData.Lens_series2 && (
        <div ref={lensSeriesTwoRef}>
          <LensPart
            lensPartData={{
              ...pageData.Lens_series2,
              lists: lensSeriesProducts.series2,
              group_id: pageData.group_id, // 统一跳转目标
            }}
            onProductClick={(product, index) =>
              trackTransitionKnowledgePageProductClick({ item: product, index })
            }
            onProductExp={(product, index) =>
              trackTransitionKnowledgePageProductExp({ item: product, index })
            }
          />
        </div>
      )}

      {/* 镜片系列3 */}
      {pageData.Lens_series3 && (
        <div ref={lensSeriesThreeRef}>
          <LensPart
            lensPartData={{
              ...pageData.Lens_series3,
              lists: lensSeriesProducts.series3,
              group_id: pageData.group_id, // 统一跳转目标
            }}
            onProductClick={(product, index) =>
              trackTransitionKnowledgePageProductClick({ item: product, index })
            }
            onProductExp={(product, index) =>
              trackTransitionKnowledgePageProductExp({ item: product, index })
            }
          />
        </div>
      )}

      {/* FAQ 区域 - 对应Vue中的"FAQ"曝光 */}
      {pageData?.faq && (
        <div ref={faqRef}>
          <FaqAccordion faqData={pageData.faq} backgroundColor="bg-[#f4f4f4]" />
        </div>
      )}
    </div>
  );
}
